import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";

import type { EditDescriptor } from "~/features/commodities/hooks/use-commodity-editing";
import type { HandlingUnit } from "~/shared/types/quote-details-types";

type BooleanCellProps = GridCustomCellProps & {
	edit: EditDescriptor;
};

// Extended type for searchable data items
type SearchableHandlingUnit = HandlingUnit & {
	originalIsStackable?: boolean;
	originalIsHazmat?: boolean;
};

export const BooleanCell = ({ edit, ...gridProps }: BooleanCellProps) => {
	const dataItem = gridProps.dataItem as SearchableHandlingUnit | undefined;

	// Guard clause: if no dataItem, render empty cell
	if (!dataItem) {
		return <td {...gridProps.tdProps} />;
	}

	const field = gridProps.field as keyof HandlingUnit;
	// Use original value if available (for searchable data), otherwise use current value
	const originalField =
		`original${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof SearchableHandlingUnit;
	const rawValue = dataItem[originalField] ?? dataItem[field];
	const actualValue =
		typeof rawValue === "boolean" ? rawValue : Boolean(rawValue);
	const isInEdit = edit[dataItem.id] === true;

	const handleChange = (event: {
		value: boolean;
		syntheticEvent: React.SyntheticEvent;
	}) => {
		if (gridProps.onChange) {
			gridProps.onChange({
				dataIndex: 0,
				dataItem: gridProps.dataItem as HandlingUnit,
				field: gridProps.field,
				syntheticEvent: event.syntheticEvent,
				value: event.value,
			});
		}
	};

	const displayValue = actualValue ? "Yes" : "No";

	return (
		<td {...gridProps.tdProps}>
			{isInEdit ? (
				<Checkbox checked={actualValue} onChange={handleChange} />
			) : (
				displayValue
			)}
		</td>
	);
};
